import {useMemo, useRef, useState} from 'react';
import {toaster} from '../components/ui/toaster';
import {DriveFile} from '../drive/types';
import {describeDriveError} from '../drive/driveApi';
import {useAddToPlazma, useFolderChildren, useRemoveFromPlazma, useRenameFile} from '../drive/queries';
import {buildPlazmaIndex, findPlazmaCopies} from '../domain/plazma';
import {settleAll} from '../drive/settleAll';
import {canDeleteAll, DELETE_FORBIDDEN_MESSAGE} from '../domain/permissions';

/** Plazma ("best of") membership and toggling for the photos of an album's web bin. */
export interface PlazmaStars {
    /** False while the plazma listing is still loading — stars can't be shown reliably yet. */
    isReady: boolean;
    isStarred: (file: DriveFile) => boolean;
    /** A copy/removal for this photo is in flight. */
    isPending: (file: DriveFile) => boolean;
    canToggle: (file: DriveFile) => boolean;
    /** Copies the photo into plazma, or deletes its plazma copy if it's already there. */
    toggle: (file: DriveFile) => Promise<void>;
    canToggleAll: (files: DriveFile[]) => boolean;
    /** If every photo is starred, deletes all their plazma copies; otherwise copies the rest into plazma. */
    toggleAll: (files: DriveFile[]) => Promise<void>;
    copiesOf: (files: DriveFile[]) => DriveFile[];
    /** Whether the user may delete the plazma copies of these web photos (see removeCopies). */
    canRemoveCopies: (webFiles: DriveFile[]) => boolean;
    /** Deletes the given plazma copies; rejects on failure (or missing permission) so callers can report it. */
    removeCopies: (copies: DriveFile[]) => Promise<void>;
    /** Renames the given plazma copies, keeping them matched to a renamed web photo. */
    renameCopies: (copies: DriveFile[], newName: string) => Promise<void>;
    /** Copies web photos into plazma again, e.g. when undoing an archive that removed their copies. */
    restoreCopies: (webFiles: DriveFile[]) => Promise<void>;
}

/** A toast title for one photo ("Plazmába másolva") or several ("3 kép plazmába másolva"). */
function countedTitle(count: number, action: string): string {
    return count === 1 ? action.charAt(0).toUpperCase() + action.slice(1) : `${count} kép ${action}`;
}

/**
 * Returns undefined when `enabled` is false (stars are only offered in the web bin).
 * `albumFolderId` is the folder that holds the bins — where plazma is created on first use.
 */
export function usePlazmaStars(
    driveId: string | undefined,
    albumFolderId: string | undefined,
    plazmaFolder: DriveFile | undefined,
    enabled: boolean,
): PlazmaStars | undefined {
    const plazmaQuery = useFolderChildren(driveId, enabled ? plazmaFolder?.id : undefined);
    const index = useMemo(() => buildPlazmaIndex(plazmaQuery.data), [plazmaQuery.data]);
    const addToPlazma = useAddToPlazma();
    const removeFromPlazma = useRemoveFromPlazma();
    const renameFile = useRenameFile();

    // The ref guards against double toggles between renders; the state drives re-rendering.
    const pendingRef = useRef<Set<string>>(new Set());
    const [pending, setPending] = useState<Set<string>>(new Set());

    function setFilesPending(files: DriveFile[], isPending: boolean) {
        const next = new Set(pendingRef.current);
        for (const file of files) {
            if (isPending) next.add(file.id); else next.delete(file.id);
        }
        pendingRef.current = next;
        setPending(next);
    }

    if (!enabled || !albumFolderId) return undefined;

    const plazmaFolderId = plazmaFolder?.id;
    const isReady = !plazmaFolderId || plazmaQuery.isSuccess;

    const allStarred = (files: DriveFile[]) => files.length > 0 && files.every((f) => index.has(f.name));
    // Un-starring deletes the plazma copies, which needs the right to delete them; starring only copies.
    const mayToggle = (files: DriveFile[]) => !allStarred(files) || canDeleteAll(findPlazmaCopies(index, files));

    const canToggleAll = (files: DriveFile[]) =>
        files.length > 0
        && isReady
        && files.every((f) => !pendingRef.current.has(f.id))
        && mayToggle(files)
        // While the plazma bin is being created, further stars would create a second one.
        && !(!plazmaFolderId && pendingRef.current.size > 0);

    async function toggleAll(files: DriveFile[]) {
        // The star buttons are disabled then, but the S shortcut can still get here.
        if (!mayToggle(files)) {
            toaster.create({type: 'error', title: DELETE_FORBIDDEN_MESSAGE});
            return;
        }
        if (!canToggleAll(files)) return;
        setFilesPending(files, true);
        try {
            if (allStarred(files) && plazmaFolderId) {
                const copies = findPlazmaCopies(index, files);
                await removeFromPlazma.mutateAsync({fileIds: copies.map((c) => c.id), plazmaFolderId});
                toaster.create({type: 'info', title: countedTitle(files.length, 'eltávolítva a plazmából'), duration: 1500});
            } else {
                const [first, ...rest] = files.filter((f) => !index.has(f.name));
                // Without a plazma bin the first copy creates it; the others wait, or they would create more.
                const created = await addToPlazma.mutateAsync({file: first, albumFolderId: albumFolderId as string, plazmaFolderId});
                await settleAll(rest.map((file) =>
                    addToPlazma.mutateAsync({file, albumFolderId: albumFolderId as string, plazmaFolderId: created.plazmaFolderId})));
                toaster.create({type: 'success', title: countedTitle(rest.length + 1, 'plazmába másolva'), duration: 1500});
            }
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        } finally {
            setFilesPending(files, false);
        }
    }

    async function removeCopies(copies: DriveFile[]) {
        if (copies.length === 0 || !plazmaFolderId) return;
        if (!canDeleteAll(copies)) throw new Error(DELETE_FORBIDDEN_MESSAGE);
        await removeFromPlazma.mutateAsync({fileIds: copies.map((c) => c.id), plazmaFolderId});
    }

    async function renameCopies(copies: DriveFile[], newName: string) {
        if (copies.length === 0 || !plazmaFolderId) return;
        await Promise.all(copies.map((c) =>
            renameFile.mutateAsync({fileId: c.id, newName, parentId: plazmaFolderId})));
    }

    async function restoreCopies(webFiles: DriveFile[]) {
        if (webFiles.length === 0) return;
        await settleAll(webFiles.map((file) =>
            addToPlazma.mutateAsync({file, albumFolderId: albumFolderId as string, plazmaFolderId})));
    }

    return {
        isReady,
        isStarred: (file) => index.has(file.name),
        isPending: (file) => pending.has(file.id),
        canToggle: (file) => canToggleAll([file]),
        toggle: (file) => toggleAll([file]),
        canToggleAll,
        toggleAll,
        copiesOf: (files) => findPlazmaCopies(index, files),
        canRemoveCopies: (webFiles) => canDeleteAll(findPlazmaCopies(index, webFiles)),
        removeCopies,
        renameCopies,
        restoreCopies,
    };
}
